import React, { useState, useEffect } from 'react';
import { 
  FileText, CheckCircle, AlertCircle, XCircle, Plus, 
  Trash2, Download, HelpCircle, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  fetchEmployeeTaxProfile, updateEmployeeTaxProfile, 
  fetchEmployeeTaxSummary, fetchTdsHistory 
} from '../../services/incomeTaxApi';

const Employee360IncomeTax = ({ employeeId }) => {
  const { hasPermission } = useAuth();
  const [taxYear] = useState('2026-27');
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tdsHistory, setTdsHistory] = useState([]);
  
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };
  const [expandedSlabs, setExpandedSlabs] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Load Profile
      try {
        const p = await fetchEmployeeTaxProfile(employeeId, taxYear);
        setProfile(p);
        
        // 2. Load summary based on profile regime
        const s = await fetchEmployeeTaxSummary(employeeId, taxYear);
        setSummary(s);
      } catch (err) {
        // If profile not found, let profile be null to force regime selection
        if (err.message.includes('No tax regime')) {
          setProfile(null);
        } else {
          setError(err.message);
        }
      }

      // 3. Load TDS History
      const hist = await fetchTdsHistory(employeeId, taxYear);
      setTdsHistory(hist);
    } catch (err) {
      setError(err.message || 'Failed to load tax details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      loadData();
    }
  }, [employeeId, taxYear]);

  // Component-level permission check
  if (!hasPermission('emp:it:view') && !hasPermission('*')) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center space-x-2">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <span>You do not have permission to view income tax details.</span>
      </div>
    );
  }

  const handleRegimeChange = async (newRegime) => {
    try {
      setLoading(true);
      await updateEmployeeTaxProfile(employeeId, taxYear, newRegime);
      setSuccess(`Tax regime updated to ${newRegime}`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update regime');
    } finally {
      setLoading(false);
    }
  };


  if (loading && !summary && !profile) {
    return <div className="py-12 text-center text-gray-500">Loading tax dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Regime Choice Box when no profile exists or to switch */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Income Tax Regime Choice</h2>
          <p className="text-sm text-gray-500 mt-1">
            Current Regime Choice for Financial Year {taxYear}: <span className="font-semibold text-blue-700">{profile?.regime || 'Not Configured'}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            type="button" 
            onClick={() => handleRegimeChange('NEW')} 
            className={`px-4 py-2 text-sm font-semibold rounded-lg border transition-colors ${
              profile?.regime === 'NEW' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            New Tax Regime
          </button>
          <button 
            type="button" 
            onClick={() => handleRegimeChange('OLD')} 
            className={`px-4 py-2 text-sm font-semibold rounded-lg border transition-colors ${
              profile?.regime === 'OLD' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Old Tax Regime
          </button>
        </div>
      </div>

      {!profile && (
        <div className="p-12 text-center bg-gray-50 border border-dashed rounded-xl text-gray-500 font-semibold">
          Please select a Tax Regime above to initiate tax worksheet calculation preview.
        </div>
      )}

      {profile && summary && (
        <>
          {/* Tax Summary Worksheet Card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm md:col-span-2">
              <h2 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2 flex justify-between items-center">
                <span>Tax Computation Projection Worksheet ({profile.regime})</span>
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">FY {taxYear}</span>
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between border-b py-2"><span className="text-gray-500">Projected Gross Income:</span><span className="font-semibold">₹{summary.projected_annual_income?.toLocaleString()}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-500">Standard Deduction:</span><span className="text-red-600 font-semibold">- ₹{summary.standard_deduction?.toLocaleString()}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-500">Taxable Net Income:</span><span className="font-bold text-blue-900">₹{summary.taxable_income?.toLocaleString()}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-500">Annual Tax Liability:</span><span className="font-semibold">₹{summary.annual_tax_liability?.toLocaleString()}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-500">TDS Deducted (YTD):</span><span className="text-green-700 font-semibold">₹{summary.tds_already_deducted?.toLocaleString()}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-500">Remaining Tax Liability:</span><span className="font-semibold">₹{summary.remaining_tax_liability?.toLocaleString()}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-500">Next Monthly TDS (Est):</span><span className="text-blue-700 font-bold">₹{summary.system_calculated_tds?.toLocaleString()}</span></div>
              </div>

              {/* Slabs breakdown details toggle */}
              <button 
                type="button" 
                onClick={() => setExpandedSlabs(!expandedSlabs)} 
                className="mt-4 inline-flex items-center gap-1 text-xs text-blue-600 font-bold hover:underline"
              >
                {expandedSlabs ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
                {expandedSlabs ? 'Hide slab calculation breakdown' : 'Show slab calculation breakdown'}
              </button>
              {expandedSlabs && (
                <div className="mt-2 bg-gray-50 p-4 rounded-lg border text-xs space-y-1.5">
                  <p className="font-semibold text-gray-700 mb-1">Tax Slab Distribution Worksheet</p>
                  {(summary.slab_calculation || []).map((slab, i) => (
                    <div key={i} className="flex justify-between text-gray-600">
                      <span>
                        Slab ₹{slab.min.toLocaleString()} - {slab.max ? `₹${slab.max.toLocaleString()}` : 'No limit'} ({slab.rate}%)
                      </span>
                      <span>
                        Taxable: ₹{slab.taxable_in_slab.toLocaleString()} → Tax: <span className="font-semibold">₹{slab.tax_in_slab.toLocaleString()}</span>
                      </span>
                    </div>
                  ))}
                  <div className="border-t pt-1.5 mt-1.5 text-gray-700 flex justify-between">
                    <span>Base Tax Before Rebate:</span><span className="font-semibold">₹{summary.tax_before_rebate?.toLocaleString()}</span>
                  </div>
                  {summary.rebate_amount > 0 && (
                    <div className="text-green-700 flex justify-between">
                      <span>Less: Section 87A Rebate:</span><span>- ₹{summary.rebate_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  {summary.surcharge_amount > 0 && (
                    <div className="text-red-600 flex justify-between">
                      <span>Add: Surcharge:</span><span>+ ₹{summary.surcharge_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="text-gray-700 flex justify-between">
                    <span>Add: Health & Education Cess (4%):</span><span className="font-semibold">+ ₹{summary.cess_amount?.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Quick Summary card */}
            <div className="bg-gradient-to-br from-blue-700 to-indigo-900 p-6 rounded-xl border border-blue-800 shadow-sm text-white flex flex-col justify-between">
              <div>
                <p className="text-blue-200 text-xs font-semibold uppercase tracking-wider">Estimated Next Deduction</p>
                <h3 className="text-4xl font-extrabold mt-1">₹{summary.final_tds?.toLocaleString()}</h3>
                <p className="text-blue-100 text-xs mt-2">Calculated over {summary.remaining_periods} months remaining in the financial year.</p>
              </div>
              <div className="mt-6 border-t border-blue-600 pt-4 text-xs text-blue-200 space-y-1">
                <div className="flex justify-between"><span>YTD Paid:</span><span className="text-white font-semibold">₹{summary.tds_already_deducted?.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Annual Projected Tax:</span><span className="text-white font-semibold">₹{summary.annual_tax_liability?.toLocaleString()}</span></div>
              </div>
            </div>
          </div>

          {/* TDS History */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Finalized TDS Monthly History</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Period</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Regime</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Taxable Gross (₹)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Standard Deduction (₹)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Annual Tax Liability (₹)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Deducted TDS (₹)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tdsHistory.map((hist, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3.5 font-semibold text-gray-900">
                        {new Date(hist.payroll_year, hist.payroll_month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3.5">{hist.regime}</td>
                      <td className="px-4 py-3.5">₹{hist.projected_annual_income?.toLocaleString()}</td>
                      <td className="px-4 py-3.5 text-red-600">- ₹{hist.standard_deduction?.toLocaleString()}</td>
                      <td className="px-4 py-3.5 font-semibold text-blue-900">₹{hist.annual_tax_liability?.toLocaleString()}</td>
                      <td className="px-4 py-3.5 font-bold text-green-700">₹{hist.final_tds?.toLocaleString()}</td>
                      <td className="px-4 py-3.5">
                        <button 
                          type="button" 
                          onClick={() => setSelectedSnapshot(hist)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-200 text-gray-700 rounded hover:bg-gray-50 font-semibold"
                        >
                          <Eye className="w-3.5 h-3.5"/> View Sheet
                        </button>
                      </td>
                    </tr>
                  ))}
                  {tdsHistory.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500 italic">No finalized payroll/TDS records found in this financial year.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Snapshot detailed view modal */}
      {selectedSnapshot && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white rounded-xl shadow-xl border w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in">
            <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
              <h3 className="font-bold text-gray-900">Historical Worksheet Snapshot</h3>
              <button type="button" onClick={() => setSelectedSnapshot(null)} className="text-gray-400 hover:text-gray-600 font-semibold">✕</button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 text-xs bg-gray-50 p-4 rounded border">
                <div><span className="text-gray-500 font-medium">Financial Year:</span> <span className="font-semibold">{selectedSnapshot.tax_year}</span></div>
                <div><span className="text-gray-500 font-medium">Tax Regime:</span> <span className="font-semibold">{selectedSnapshot.regime}</span></div>
                <div><span className="text-gray-500 font-medium">Annual Taxable Income:</span> <span className="font-semibold">₹{selectedSnapshot.taxable_income?.toLocaleString()}</span></div>
                <div><span className="text-gray-500 font-medium">Final Rounded Liability:</span> <span className="font-bold text-blue-900">₹{selectedSnapshot.annual_tax_liability?.toLocaleString()}</span></div>
                <div><span className="text-gray-500 font-medium">YTD TDS Paid Prior:</span> <span className="font-semibold text-green-700">₹{selectedSnapshot.tds_already_deducted?.toLocaleString()}</span></div>
                <div><span className="text-gray-500 font-medium">Final TDS Deducted:</span> <span className="font-bold text-green-700">₹{selectedSnapshot.final_tds?.toLocaleString()}</span></div>
              </div>

              {selectedSnapshot.adjustment_amount !== 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded text-xs">
                  <p className="font-bold">Manual Adjustment Applied:</p>
                  <p>Adjustment: ₹{selectedSnapshot.adjustment_amount?.toLocaleString()}</p>
                  <p>Reason: {selectedSnapshot.adjustment_reason || 'No reason provided'}</p>
                </div>
              )}

              <div className="text-xs">
                <p className="font-bold text-gray-900 mb-2">Detailed Slab Breakdown</p>
                <div className="space-y-1.5">
                  {(selectedSnapshot.slab_calculation || []).map((slab, index) => (
                    <div key={index} className="flex justify-between text-gray-600 border-b pb-1">
                      <span>Slab ₹{slab.min.toLocaleString()} - {slab.max ? `₹${slab.max.toLocaleString()}` : 'No limit'} ({slab.rate}%)</span>
                      <span>Taxable: ₹{slab.taxable_in_slab?.toLocaleString()} → Tax: <span className="font-semibold text-gray-900">₹{slab.tax_in_slab?.toLocaleString()}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
              <button type="button" onClick={() => setSelectedSnapshot(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-semibold">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employee360IncomeTax;
